import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface Passenger {
  name: string;
  seat_number?: string;
  ticket_number?: string;
  frequent_flyer_number?: string;
  meal_preference?: string;
}

export interface TrainingFlightData {
  airline?: string;
  flight_number?: string;
  departure_airport?: string;
  arrival_airport?: string;
  departure_time?: string;
  arrival_time?: string;
  confirmation_number?: string;
  cabin_class?: string;
  passengers?: Passenger[];
}

export interface TrainingHotelData {
  hotel_name?: string;
  address?: string;
  city?: string;
  check_in_date?: string;
  check_out_date?: string;
  confirmation_number?: string;
  room_type?: string;
  guests?: { name: string }[];
}

export interface TrainingCarData {
  rental_company?: string;
  pickup_location?: string;
  dropoff_location?: string;
  pickup_time?: string;
  dropoff_time?: string;
  confirmation_number?: string;
  car_type?: string;
}

export interface TrainingActivityData {
  name?: string;
  date?: string;
  time?: string;
  location?: string;
  confirmation_number?: string;
}

export interface TrainingExtractedData {
  display_name?: string;
  destination?: string;
  flights?: TrainingFlightData[];
  hotels?: TrainingHotelData[];
  cars?: TrainingCarData[];
  activities?: TrainingActivityData[];
}

export interface ParserTrainingExample {
  id: string;
  created_at: string;
  created_by: string;
  email_subject: string | null;
  email_content: string;
  extracted_data: TrainingExtractedData;
  booking_type: "flight" | "hotel" | "car" | "activity";
  is_active: boolean;
}

export function useParserTrainingExamples() {
  return useQuery({
    queryKey: ["parserTrainingExamples"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("parser_training_examples")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as ParserTrainingExample[];
    },
  });
}

export function useCreateTrainingExample() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (example: {
      email_subject: string | null;
      email_content: string;
      extracted_data: TrainingExtractedData;
      booking_type: "flight" | "hotel" | "car" | "activity";
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("parser_training_examples")
        .insert({
          created_by: user.id,
          email_subject: example.email_subject,
          email_content: example.email_content,
          extracted_data: example.extracted_data as unknown as Record<string, unknown>,
          booking_type: example.booking_type,
          is_active: true,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["parserTrainingExamples"] });
      toast.success("Training example added");
    },
    onError: (error) => {
      toast.error("Failed to add example: " + error.message);
    },
  });
}

export function useUpdateTrainingExample() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...updates
    }: {
      id: string;
      email_subject?: string | null;
      email_content?: string;
      extracted_data?: TrainingExtractedData;
      booking_type?: "flight" | "hotel" | "car" | "activity";
      is_active?: boolean;
    }) => {
      const updatePayload: Record<string, unknown> = { ...updates };
      if (updates.extracted_data) {
        updatePayload.extracted_data = updates.extracted_data as unknown as Record<string, unknown>;
      }

      const { data, error } = await supabase
        .from("parser_training_examples")
        .update(updatePayload)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["parserTrainingExamples"] });
      toast.success("Training example updated");
    },
    onError: (error) => {
      toast.error("Failed to update example: " + error.message);
    },
  });
}

export function useDeleteTrainingExample() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("parser_training_examples")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["parserTrainingExamples"] });
      toast.success("Training example deleted");
    },
    onError: (error) => {
      toast.error("Failed to delete example: " + error.message);
    },
  });
}
